import * as React from "react";
import { mergeClasses } from "@fluentui/react-components";
import { useFormPrimitiveStyles } from "../formPrimitives";
import { SperrWert } from "../SperrWert";
import { aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Generisches Eingabefeld mit Validierung/Normalisierung im REFINED-Look
 * (gleiche Struktur/Styles wie LabeledInput → optisch identisch). Basis für
 * Phone/Email/IBAN/BIC/USt-IdNr/Steuernummer.
 * - `sanitize`: filtert beim Tippen (z. B. Buchstaben raus).
 * - `normalizeOnBlur`: Speicherform beim Verlassen (z. B. E.164, gruppierte IBAN).
 * - `validate`: liefert {valid,error}; Fehler erscheint touched-gated darunter.
 * - `onCommit`: Callback mit dem (normalisierten) Wert beim Blur (Cross-Field).
 * - `formatOnType`: formatiert bereits BEIM TIPPEN und gibt die Cursor-Position
 *   mit zurück (IBAN in 4er-Gruppen). Ersetzt `sanitize`; ohne die
 *   Cursor-Rückgabe würde die Einfügemarke bei jedem Zeichen ans Ende springen,
 *   sobald die Formatierung den Wert verändert.
 */

type ValResult = { valid: boolean; error?: string };

const ErrIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="8" cy="8" r="6.4" />
    <path d="M8 4.8v3.6" strokeLinecap="round" />
    <circle cx="8" cy="11.1" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

export function ValidatedField({
  label,
  name,
  value,
  onChange,
  placeholder,
  required,
  type = "text",
  leftIcon,
  rightSlot,
  labelRightSlot,
  inputMode,
  validate,
  sanitize,
  normalizeOnBlur,
  onCommit,
  formatOnType,
  unterFeld,
}: {
  label: React.ReactNode;
  name: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: "text" | "email" | "tel" | "number";
  leftIcon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  labelRightSlot?: React.ReactNode;
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>["inputMode"];
  validate?: (v: string) => ValResult;
  sanitize?: (v: string) => string;
  normalizeOnBlur?: (v: string) => string;
  onCommit?: (v: string) => void;
  formatOnType?: (vorher: string, nachher: string, cursor: number) => { wert: string; cursor: number };
  /** Zusatz unter dem Feld (z. B. der Hinweis „Diese E-Mail-Adresse steht schon unter …"). */
  unterFeld?: React.ReactNode;
}) {
  const s = useFormPrimitiveStyles();
  const [touched, setTouched] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const zielCursor = React.useRef<number | null>(null);

  // Nach dem Commit des neuen Werts, aber vor dem Paint: sonst sieht man den
  // Cursor kurz am Ende stehen und zurückspringen.
  React.useLayoutEffect(() => {
    const ziel = zielCursor.current;
    zielCursor.current = null;
    const el = inputRef.current;
    if (ziel == null || !el || aktivesDokument().activeElement !== el) return;
    el.setSelectionRange(ziel, ziel);
  }, [value]);
  const res = validate ? validate(value) : { valid: true };
  const showError = touched && !res.valid;

  const handleBlur = () => {
    setTouched(true);
    let next = value;
    if (normalizeOnBlur) {
      next = normalizeOnBlur(value);
      if (next !== value) onChange(next);
    }
    if (onCommit) onCommit(next);
  };

  const inputEl = (
    <div className={s.inputWrap}>
      {leftIcon && <span className={s.inputIcon}>{leftIcon}</span>}
      <input
        ref={inputRef}
        id={name}
        name={name}
        type={type}
        inputMode={inputMode}
        // CRM-Felder (IBAN/BIC/E-Mail/Telefon …) tragen Kundendaten – kein
        // Browser-Autofill. Siehe LabeledInput.
        autoComplete="off"
        className={mergeClasses(s.input, !!leftIcon && s.inputWithIcon, showError && s.inputInvalid)}
        value={value}
        placeholder={placeholder}
        aria-invalid={showError || undefined}
        onChange={(e) => {
          if (formatOnType) {
            const el = e.target;
            const ergebnis = formatOnType(value, el.value, el.selectionStart ?? el.value.length);
            zielCursor.current = ergebnis.cursor;
            onChange(ergebnis.wert);
            return;
          }
          onChange(sanitize ? sanitize(e.target.value) : e.target.value);
        }}
        onBlur={handleBlur}
      />
      <SperrWert />
    </div>
  );

  return (
    <div className={s.field}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <label className={s.label} htmlFor={name}>
          {label}
          {required && <span className={s.req}> *</span>}
        </label>
        {labelRightSlot}
      </div>
      {rightSlot ? (

        <div className={s.fieldWithSlot}>
          {inputEl}
          {rightSlot}
        </div>
      ) : (
        inputEl
      )}
      {showError && res.error && (
        <div className={s.errorMsg}>
          <ErrIcon />
          {res.error}
        </div>
      )}
      {unterFeld}
    </div>
  );
}

export default ValidatedField;
